// Invoice detail — Wave D.
//
// Read-only view of a single invoice with line items, payment status, and
// the shared <PdfActions /> component for view/download/print.

import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Pencil, Send } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { StatusBadge } from '../../components/ui/StatusBadge/StatusBadge';
import { PdfActions } from '../../components/ui/PdfActions/PdfActions';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { endpoints } from '../../api/endpoints';
import { useInvoiceDetail, useSendInvoice } from './hooks/useInvoices';
import { useAuthStore } from '../../store/authStore';
import type { BadgeVariant } from '../../components/ui/Badge/Badge';
import type { InvoiceStatus } from '../../types/api';
import styles from './InvoiceDetailPage.module.css';

const STATUS_MAP: Record<InvoiceStatus, BadgeVariant> = {
  draft: 'muted',
  issued: 'info',
  paid: 'success',
  overdue: 'danger',
  cancelled: 'warning',
};

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  issued: 'Issued',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
};

function formatMoney(n: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function InvoiceDetailPage(): JSX.Element {
  const { enc_id: encIdParam, id: idParam } = useParams<{ enc_id?: string; id?: string }>();
  const encId = encIdParam ?? idParam;
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role ?? null);
  const isAdmin = role === 'admin';

  const detail = useInvoiceDetail(encId);
  const sendMutation = useSendInvoice();

  if (detail.isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader title="Invoice" />
        <div className={styles.loading}>
          <Spinner />
        </div>
      </div>
    );
  }

  const invoice = detail.data?.invoice;
  if (!invoice) {
    return (
      <div className={styles.page}>
        <PageHeader title="Invoice not found" />
        <Card>
          <p>The invoice you requested could not be loaded.</p>
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/collections/invoices')}>
            Back to list
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={`Invoice ${invoice.invoice_number}`}
        subtitle={
          <span className={styles.subRow}>
            <StatusBadge
              status={invoice.status}
              statusMap={STATUS_MAP}
              formatLabel={(s) => STATUS_LABELS[s] ?? s}
            />
            <span>· Issued {formatDate(invoice.issue_date)}</span>
            {invoice.due_date ? <span>· Due {formatDate(invoice.due_date)}</span> : null}
          </span>
        }
        actions={
          <div className={styles.actions}>
            <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/collections/invoices')}>
              Back
            </Button>
            <Button
              variant="secondary"
              icon={Send}
              onClick={() => sendMutation.mutate({ encId: invoice.enc_id })}
              loading={sendMutation.isPending}
            >
              Send
            </Button>
            {isAdmin ? (
              <Button
                variant="primary"
                icon={Pencil}
                onClick={() => navigate(`/collections/invoices/${encodeURIComponent(invoice.enc_id)}/edit`)}
              >
                Edit
              </Button>
            ) : null}
          </div>
        }
      />

      <div className={styles.grid}>
        <Card title="Customer">
          <dl className={styles.dl}>
            <div>
              <dt>Name</dt>
              <dd>{invoice.customer_name ?? '—'}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{invoice.customer_email ?? '—'}</dd>
            </div>
            <div>
              <dt>Address</dt>
              <dd>
                {invoice.customer_address ?? '—'}
                {invoice.customer_postcode ? `, ${invoice.customer_postcode}` : ''}
              </dd>
            </div>
          </dl>
        </Card>

        <Card title="Totals">
          <dl className={styles.dl}>
            <div>
              <dt>Subtotal</dt>
              <dd>{formatMoney(invoice.subtotal)}</dd>
            </div>
            <div>
              <dt>Tax</dt>
              <dd>{formatMoney(invoice.tax_amount)}</dd>
            </div>
            <div>
              <dt>Total</dt>
              <dd>
                <strong>{formatMoney(invoice.total_amount)}</strong>
              </dd>
            </div>
            <div>
              <dt>Paid</dt>
              <dd>{formatMoney(invoice.amount_paid)}</dd>
            </div>
            <div>
              <dt>Outstanding</dt>
              <dd>{formatMoney(invoice.total_amount - invoice.amount_paid)}</dd>
            </div>
          </dl>
        </Card>

        <Card title="PDF">
          <PdfActions
            endpoint={endpoints.invoices.pdf(invoice.enc_id)}
            filename={`invoice-${invoice.invoice_number}.pdf`}
          />
        </Card>
      </div>

      <Card title="Line items" flush>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Description</th>
              <th className={styles.colNum}>Qty</th>
              <th className={styles.colNum}>Unit price</th>
              <th className={styles.colNum}>Tax %</th>
              <th className={styles.colNum}>Total</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((item) => (
              <tr key={item.line_number}>
                <td>{item.line_number}</td>
                <td>{item.description}</td>
                <td className={styles.colNum}>{item.quantity}</td>
                <td className={styles.colNum}>{formatMoney(item.unit_price)}</td>
                <td className={styles.colNum}>{item.tax_rate}%</td>
                <td className={styles.colNum}>{formatMoney(item.line_total)}</td>
              </tr>
            ))}
            {invoice.items.length === 0 ? (
              <tr>
                <td colSpan={6} className={styles.empty}>
                  No line items.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </Card>

      {invoice.notes ? (
        <Card title="Notes">
          <p className={styles.notes}>{invoice.notes}</p>
        </Card>
      ) : null}
    </div>
  );
}
